// @architect-approved: DIRECTORY_HOST_PATTERN - Uses CR2 registry (apiRequest) data source, not manifest-based directory resolution
// @architect-approved: HARDCODED_COLORS - Floor-based color fallback default
// (same spirit as the /dirm_ exemption, filename doesn't match that path substring).
/**
 * DirmLeftMenuParentChildPattern — LEFT MENU with Parent-Child Navigation Data
 *
 * Left overlay menu with accordion floor sections.
 * Data source: /api/cr2-registry @Manage=12 (Component_Registry2 with IncludeIn_UserNav=1)
 * Same visual structure as DirmLeftMenuPattern, different data source.
 * 
 * Layout: Drawer sidebar (left 320px) + main content area (right flex-grow)
 * Accordion sections grouped by floor, modules organized by floor_number.
 * 
 * Props: seq (Component_Sequence_Num) → identifies this directory component
 *
 * Created: 2026-07-16
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiRequest } from '../../../utils/apiService';
import { FLOOR_COLORS } from './icon-map';

interface DirmLeftMenuParentChildPatternProps {
    seq: number;
}

interface CR2Record {
    sequence_num: number;
    prefix: string;
    friendly_name: string;
    floor_number: number;
    tsxfilename_target: string;
    prop_dependant_parent_seq?: number | null;
    includein_usernav?: number;
}

interface ModuleButton {
    seq: number;
    floor: string;
    floorNum: number;
    label: string;
    color: string;
    prefix: string;
    tsx_file_name_target: string;
    isParent: boolean;
}

const DRAWER_WIDTH = 320;

const FLOOR_NAMES: Record<number, string> = {
    0: 'Ground Floor',
    1: 'Floor 1',
    2: 'Floor 2 - Collaboration',
    3: 'Floor 3 - Tenant Offices',
    4: 'Floor 4 - TG Solutions',
    5: 'Floor 5',
    6: 'Floor 6',
};

const DirmLeftMenuParentChildPattern: React.FC<DirmLeftMenuParentChildPatternProps> = ({ seq }) => {
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [expanded, setExpanded] = useState<string | false>('02'); // Default to Floor 2
    const [modules, setModules] = useState<CR2Record[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load modules from API
    const loadModules = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response: any = await apiRequest('/api/cr2-registry', 'POST', { manage: 12 });
            const rawData = Array.isArray(response) ? response : response?.data || [];

            // Transform: Parse floor_number string -> number
            const data = rawData.map((r: any) => ({
                ...r,
                floor_number: parseInt(r.floor_number, 10) || 0,
            }));

            console.log(`User Nav Left Menu loaded ${data.length} modules (@Manage=12)`);
            setModules(data);
        } catch (err: any) {
            console.error('User Nav Left Menu load error:', err);
            setError(err?.message || 'Failed to load navigation modules');
            setModules([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadModules();
    }, [loadModules]);

    // Map modules to buttons, filter parents only (prop_dependant_parent_seq is null)
    const moduleButtons = useMemo(() => {
        return modules
            .filter((m) => !m.prop_dependant_parent_seq) // Parents only
            .map(
                (mod): ModuleButton => {
                    const floorNum = Number(mod.floor_number) || 0;
                    return {
                        seq: mod.sequence_num,
                        floor: String(floorNum).padStart(2, '0'),
                        floorNum,
                        label: mod.friendly_name,
                        color: FLOOR_COLORS[floorNum] || '#607D8B',
                        prefix: mod.prefix,
                        tsx_file_name_target: mod.tsxfilename_target,
                        isParent: true,
                    };
                },
            )
            .sort((a, b) => {
                // Sort by floor first, then by label
                if (a.floorNum !== b.floorNum) return a.floorNum - b.floorNum;
                return a.label.localeCompare(b.label);
            });
    }, [modules]);

    // Group buttons by floor
    const floorGroups = useMemo(() => {
        const groups: Record<string, ModuleButton[]> = {};

        for (const btn of moduleButtons) {
            if (!groups[btn.floor]) {
                groups[btn.floor] = [];
            }
            groups[btn.floor].push(btn);
        }

        return groups;
    }, [moduleButtons]);

    const handleFloorChange = (floor: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? floor : false);
    };

    const handleButtonClick = (btn: ModuleButton) => {
        // Navigate to module route
        const route = `/scr_tgc_${btn.seq}`;
        console.log(`Navigating to: ${route}`);
        navigate(route);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
                <IconButton onClick={loadModules} sx={{ mt: 2 }}>
                    <RefreshIcon />
                </IconButton>
            </Box>
        );
    }

    const totalModules = moduleButtons.length;
    const totalFloors = Object.keys(floorGroups).length;

    return (
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            {/* Left Drawer */}
            <Drawer
                variant="persistent"
                anchor="left"
                open={drawerOpen}
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        bgcolor: 'background.default',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <FolderIcon />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{
                            fontWeight: 600
                        }}>
                            User Navigation
                        </Typography>
                        <Typography variant="caption">
                            {totalFloors} Floors • {totalModules} Modules
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={loadModules}
                        sx={{ color: 'primary.contrastText' }}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Divider />

                {/* Floor Accordions */}
                <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                    {Object.keys(floorGroups)
                        .sort()
                        .map((floor) => {
                            const buttons = floorGroups[floor];
                            const floorNum = parseInt(floor, 10);
                            const floorName = FLOOR_NAMES[floorNum] || `Floor ${floorNum}`;
                            const floorColor = FLOOR_COLORS[floorNum] || '#607D8B';

                            return (
                                <Accordion
                                    key={floor}
                                    expanded={expanded === floor}
                                    onChange={handleFloorChange(floor)}
                                    disableGutters
                                    sx={{
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            borderLeft: '4px solid',
                                            borderColor: floorColor,
                                            minHeight: 48,
                                            '&.Mui-expanded': {
                                                minHeight: 48,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                width: '100%',
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{
                                                fontWeight: 600
                                            }}>
                                                {floorName}
                                            </Typography>
                                            <Chip
                                                label={buttons.length}
                                                size="small"
                                                sx={{ ml: 'auto', mr: 1 }}
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 0 }}>
                                        <List disablePadding>
                                            {buttons.map((btn) => (
                                                <ListItemButton
                                                    key={btn.seq}
                                                    onClick={() => handleButtonClick(btn)}
                                                    sx={{
                                                        pl: 2,
                                                        pr: 1,
                                                        py: 0.25,
                                                        minHeight: 32,
                                                        '&:hover': {
                                                            bgcolor: 'action.hover',
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <Chip
                                                            label={btn.prefix.toUpperCase()}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: btn.color,
                                                                color: 'white',
                                                                fontSize: '0.6rem',
                                                                height: 18,
                                                                '& .MuiChip-label': {
                                                                    px: 0.5,
                                                                },
                                                            }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={`${btn.label} [${btn.seq}]`}
                                                        slotProps={{
                                                            primary: {
                                                                variant: 'body2',
                                                                noWrap: true,
                                                                sx: { fontSize: '0.8rem' },
                                                            }
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                </Box>

                {/* Footer */}
                <Divider />
                <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                    <Typography
                        variant="caption"
                        align="center"
                        sx={{
                            color: "text.secondary",
                            display: "block"
                        }}>
                        User Nav Pattern • Seq {seq}
                    </Typography>
                </Box>
            </Drawer>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    ml: drawerOpen ? 0 : `-${DRAWER_WIDTH}px`,
                    transition: 'margin 0.3s',
                    overflow: 'auto',
                }}
            >
                {/* Toggle Menu Button */}
                <IconButton
                    onClick={() => setDrawerOpen(!drawerOpen)}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: drawerOpen ? DRAWER_WIDTH + 16 : 16,
                        zIndex: 1200,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        transition: 'left 0.3s',
                        '&:hover': { bgcolor: 'action.hover' },
                    }}
                >
                    <MenuIcon />
                </IconButton>

                <Paper sx={{ p: 4, mt: 8, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        User Navigation
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            marginBottom: "16px"
                        }}>
                        Select an option from the left menu to navigate the platform.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                        <Chip label={`${totalFloors} Floors`} color="primary" />
                        <Chip label={`${totalModules} Total Modules`} />
                        <Chip label={`Seq ${seq}`} variant="outlined" />
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default DirmLeftMenuParentChildPattern;
